import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslate } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { HelperText01 } from 'module/ui/components/styles';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { Button, Dialog } from '@rocketlane/ui';
import { InputRef } from 'antd';
import {
  GetSignalConfigurationFromPayload,
  SignalType,
} from 'api/signal/types';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SignalTypeSelector } from './signal-type/signal-selector';

const Hint = styled.div`
  ${HelperText01};
  color: var(--text-secondary);
`;
const Wrapper = styled.div`
  ${Hint} {
    margin-top: var(--spacing-02);
  }
`;
const FooterWrapper = styled(CenterAlignedBox)`
  justify-content: flex-end;
  .rl-btn {
    margin-left: 0;
  }
`;

type FormValues = {
  signalName: string;
  signalType: SignalType;
};

interface Props {
  onSubmit: (payload: GetSignalConfigurationFromPayload) => Promise<void>;
  onClose: VoidFunction;
}
export const SignalPreConfigurationModal: FC<Props> = ({
  onSubmit,
  onClose,
}) => {
  const translate = useTranslate();
  const [form] = Form.useForm<FormValues>();
  const [actionLoading, setActionLoading] = useState(false);
  const [showHintFor, setShowHintFor] = useState<SignalType>();

  const onNext = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        setActionLoading(true);
        onSubmit({
          signalName: values.signalName,
          signalType: values.signalType,
        }).finally(() => setActionLoading(false));
      })
      .catch(() => {});
  }, [onSubmit, form]);
  const [isFormValid, setIsFormValid] = useState(false);
  const onFormValuesChange = useCallback(() => {
    queueMacrotask(() => {
      form
        .validateFields()
        .then(() => {
          setIsFormValid(true);
        })
        .catch(() => {
          setIsFormValid(false);
        });
    });
  }, [form]);

  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Dialog
      overlayClosable={!actionLoading}
      closable={!actionLoading}
      open
      onCancel={onClose}
      title={
        <FormattedMessage id="settings.signal_configuration.signal_name_modal.title" />
      }
      footer={
        <FooterWrapper>
          <Button onClick={onClose} disabled={actionLoading}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            variant="primary"
            rightIcon={<FontAwesomeIcon icon={faArrowRight} size={12} />}
            disabled={!isFormValid}
            onClick={onNext}
            loading={actionLoading}
          >
            {actionLoading ? (
              <FormattedMessage id="thinking" />
            ) : (
              <FormattedMessage id="next" />
            )}
          </Button>
        </FooterWrapper>
      }
      content={
        <Wrapper>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={onFormValuesChange}
          >
            {/* This is not used anymore, but keeping it here for reference */}
            {/* <Form.Item
            name="signalName"
            label={<FormattedMessage id="name" />}
            rules={[
              {
                validator: (_, value) => {
                  if (isEmpty(value)) {
                    return Promise.reject(
                      new Error(translate('input.required')),
                    );
                  }
                  if (isDuplicateSignalName(value)) {
                    return Promise.reject(
                      new Error(
                        translate(
                          'settings.signal_configuration.signal_name_modal.name.errors.already_exists',
                          { signalName: value },
                        ),
                      ),
                    );
                  } else {
                    return Promise.resolve();
                  }
                },
              },
            ]}
            extra={
              <FormattedMessage id="settings.signal_configuration.signal_name_modal.name.hint" />
            }
          >
            <Input
              placeholder={placeholderText}
              disabled={actionLoading}
              ref={inputRef}
            />
          </Form.Item> */}
            <Form.Item shouldUpdate>
              {({ getFieldValue }) => (
                <Form.Item
                  name="signalType"
                  label={
                    <FormattedMessage id="settings.signal_configuration.signal_name_modal.type.label" />
                  }
                  rules={[
                    {
                      required: true,
                      message: translate('input.required'),
                    },
                  ]}
                  extra={(() => {
                    const hovered = showHintFor;
                    const selected = getFieldValue('signalType') as
                      | SignalType
                      | undefined;
                    const hintType = hovered ?? selected;

                    if (!hintType) {
                      return null;
                    }

                    return (
                      <FormattedMessage
                        id={`settings.signal_configuration.signal_name_modal.type.${hintType.toLowerCase()}.hint`}
                      />
                    );
                  })()}
                >
                  <SignalTypeSelector onHover={setShowHintFor} />
                </Form.Item>
              )}
            </Form.Item>
          </Form>
        </Wrapper>
      }
    />
  );
};
